// スマホ→Drive アップロード（Drive 完全双方向フェーズ2・手動）。
// PC(Electron) の src/cloud/drive/push.js と「同じ Drive 形式」で1項目を書く：
//   - メディア本体: <name>.<ext>
//   - バンドル:     <name>.bundle.json  = { metaContent, files{ <name>: JSON文字列 } }
//   - catalog.json: items[hash] = { ver, name, kind, hasSubtitles, durationSec, sizeBytes, mediaFile, bundleFile, folderId }
// v1: 手動・1件ずつ・folderId=null（ルート）・catalog は LWW 読み書き（厳密マージは後フェーズ）。
import { db } from './db.js'
import * as gdrive from './gdrive.js'
import { getMediaFile } from './opfs.js'
import { getSyncPrefs, isLikelyWifi } from './syncprefs.js'

const MIME = {
  mp4: 'video/mp4', m4v: 'video/mp4', webm: 'video/webm', ogv: 'video/ogg',
  mov: 'video/quicktime', mkv: 'video/x-matroska',
  mp3: 'audio/mpeg', m4a: 'audio/mp4', aac: 'audio/aac', wav: 'audio/wav',
  flac: 'audio/flac', ogg: 'audio/ogg', oga: 'audio/ogg', opus: 'audio/ogg',
}

// Drive/Windows で安全なファイル名に（push.js の sanitize 相当）。
function sanitize(name) {
  return (String(name || 'item').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, ' ').trim().slice(0, 120)) || 'item'
}

// 端末ローカル項目(hash)を Drive へアップロード。onProgress({phase}) で進捗。
export async function uploadLocalItem(hash, onProgress) {
  const report = (phase) => onProgress && onProgress({ phase })
  const d = await db()
  const it = await d.get('items', hash)
  if (!it) throw new Error('項目が見つかりません')
  const file = await getMediaFile(hash)
  if (!file) throw new Error('メディアが端末にありません')

  const ext = (it.mediaExt || (file.name.split('.').pop() || '')).toLowerCase()
  const base = sanitize(it.name)
  const mediaName = ext ? `${base}.${ext}` : base
  const bundleName = `${base}.bundle.json`

  // バンドルの files: tracks ストアの `${hash}/*`（字幕/トラック等＝JSON文字列のまま）＋ state.json。
  const files = {}
  const tx = d.transaction('tracks')
  let cur = await tx.store.openCursor()
  while (cur) {
    const k = String(cur.key)
    if (k.startsWith(hash + '/')) files[k.slice(hash.length + 1)] = cur.value
    cur = await cur.continue()
  }
  const st = await d.get('state', hash)
  if (st) {
    files['state.json'] = JSON.stringify({
      pos: st.pos || 0,
      bookmarks: st.bookmarks || [],
      updatedAt: st.updatedAt || new Date().toISOString(),
    })
  }

  const metaContent = {
    ...(it.metaContent || {}),
    name: it.name,
    kind: it.kind,
    duration: it.duration || 0,
    hasSubtitles: !!it.hasSubtitles,
    folderId: it.folderId || null, // フォルダ所属を Drive に伝播（段2）
  }

  // 1) メディア本体
  report('media')
  await gdrive.uploadBlob(mediaName, file, MIME[ext] || 'application/octet-stream')

  // 2) バンドル
  report('bundle')
  await gdrive.putJSON(bundleName, { metaContent, files })

  // 3) catalog（多ライター安全マージ＝最新へ自分の項目だけ足す。他端末の項目を取りこぼさない）
  report('catalog')
  const merged = await gdrive.mergeUpdateJSON('catalog.json', (cur) => {
    const items = (cur && cur.items) || {}
    const prev = items[hash] || {}
    items[hash] = {
      ver: (prev.ver || 0) + 1,
      name: it.name,
      kind: it.kind,
      hasSubtitles: !!it.hasSubtitles,
      durationSec: it.duration || 0,
      sizeBytes: file.size || 0,
      mediaFile: mediaName,
      bundleFile: bundleName,
      folderId: it.folderId || null,
    }
    return { schemaVersion: (cur && cur.schemaVersion) || 1, items }
  })

  // ローカル項目に「アップ済み」印を付ける（同期版＝catalog の ver に合わせる）。
  it.uploadedToDrive = true
  it.syncVer = (merged.items[hash] && merged.items[hash].ver) || 1
  await d.put('items', it)
  report('done')
  return { ok: true }
}

// Drive からも削除：メディア＋bundle を delete し、catalog を tombstone(deleted:true, ver++) に。
// 他端末は次の同期で catalog の deleted を見て端末からも消す。要連携（未連携は呼ばない）。
export async function deleteFromDrive(hash) {
  if (!gdrive.isConnected()) return { ok: false, reason: 'not-connected' }
  let entry = null
  try {
    const c = await gdrive.getJSON('catalog.json')
    entry = (c.items || {})[hash] || null
  } catch (e) {
    if (e.name !== 'NotFound') throw e
  }
  if (entry) {
    if (entry.mediaFile) { try { await gdrive.deleteByName(entry.mediaFile) } catch { /* 無ければ無視 */ } }
    if (entry.bundleFile) { try { await gdrive.deleteByName(entry.bundleFile) } catch { /* noop */ } }
  }
  await gdrive.mergeUpdateJSON('catalog.json', (cur) => {
    const items = (cur && cur.items) || {}
    const prev = items[hash] || {}
    items[hash] = { deleted: true, ver: (prev.ver || 0) + 1, name: prev.name || '' } // 墓標
    return { schemaVersion: (cur && cur.schemaVersion) || 1, items }
  })
  return { ok: true }
}

// 項目のフォルダ移動を Drive catalog に反映（アップ済み項目のみ）。未アップ/未連携は何もしない。
export async function updateItemFolder(hash, folderId) {
  if (!gdrive.isConnected()) return
  const d = await db()
  const it = await d.get('items', hash)
  if (!it || !it.uploadedToDrive) return // まだ Drive に無い項目は次回アップ時に folderId が乗る
  await gdrive.mergeUpdateJSON('catalog.json', (cur) => {
    const items = (cur && cur.items) || {}
    if (items[hash]) {
      items[hash] = { ...items[hash], folderId: folderId || null, ver: (items[hash].ver || 0) + 1 }
    }
    return { schemaVersion: (cur && cur.schemaVersion) || 1, items }
  })
}

// 自動アップロード：連携済なら、未アップの端末ローカル項目を全部 Drive に上げる
// （連携済→全部上がる/例外なし＝合意の挙動。連携直後のバックフィル・取り込み後・起動時に呼ぶ）。
// Wi-Fi 設定だけは尊重（'wifi' でモバイル時はスキップ＝次に Wi-Fi になったら上がる）。失敗は次回リトライ。
let _autoRunning = false
export async function autoUploadAll(onProgress) {
  if (_autoRunning) return { skipped: true, reason: 'running' }
  if (!gdrive.isConnected()) return { skipped: true, reason: 'not-connected' }
  const sp = await getSyncPrefs()
  if (sp.autoUpload === 'wifi' && !isLikelyWifi()) return { skipped: true, reason: 'wifi-wait' }
  _autoRunning = true
  let uploaded = 0
  try {
    const d = await db()
    const all = await d.getAll('items')
    const pending = all.filter((m) => m.origin === 'local' && !m.uploadedToDrive)
    for (const it of pending) {
      try {
        await uploadLocalItem(it.hash, (p) => onProgress && onProgress({ ...p, name: it.name }))
        uploaded++
      } catch (e) {
        // 失敗は uploadedToDrive が立たないので残る＝次回トリガで再試行。
        console.error('[drive] 自動アップロード失敗:', it.name, e.message || e)
      }
    }
  } finally {
    _autoRunning = false
  }
  return { ok: true, uploaded }
}
